import React from 'react';
import { Modal, Box, Typography, IconButton, Divider, Button } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 800,
  maxHeight: '90vh',
  bgcolor: 'background.paper',
  borderRadius: '12px',
  boxShadow: 24,
  p: 4,
  overflowY: 'auto',
};

export default function PlayerDetailModal({ open, handleClose, player, onViewDetails }) {
  if (!player) return null;

  // Function to classify player type based on scores
  const classifyPlayer = (performance, culturalFit) => {
    if (performance >= 1 && performance <= 3 && culturalFit >= 1 && culturalFit <= 2) {
      return 'Jugador A'; // Altamente alineados
    } else if (performance >= 1 && performance <= 3 && culturalFit >= 3 && culturalFit <= 4) {
      return 'Jugador B'; // Buen rendimiento, bajo fit
    } else if (performance >= 4 && performance <= 5 && culturalFit >= 1 && culturalFit <= 2) {
      return 'Jugador C'; // Buen fit, bajo rendimiento
    } else if (performance >= 4 && performance <= 5 && culturalFit >= 3 && culturalFit <= 4) {
      return 'Jugador D'; // Doble riesgo
    }
    return 'Desconocido';
  };

  // Function to generate a simulated AI recommendation
  const generateAIRecommendation = (performance, culturalFit) => {
    if (performance >= 1 && performance <= 3 && culturalFit >= 1 && culturalFit <= 2) {
      return "Este colaborador demuestra un alto rendimiento y un excelente ajuste cultural. Recomendamos mantener el enfoque en el desarrollo de liderazgo y la mentoría para maximizar su potencial.";
    } else if (performance >= 1 && performance <= 3 && culturalFit >= 3 && culturalFit <= 4) {
      return "Este colaborador tiene un buen desempeño, pero su ajuste cultural podría mejorarse. Recomendamos enfocar el desarrollo en áreas de valores y trabajo en equipo para una mejor integración.";
    } else if (performance >= 4 && performance <= 5 && culturalFit >= 1 && culturalFit <= 2) {
      return "Este colaborador tiene un alto ajuste cultural, pero su rendimiento necesita atención. Recomendamos un plan de mejora de desempeño enfocado en habilidades técnicas o productividad.";
    } else if (performance >= 4 && performance <= 5 && culturalFit >= 3 && culturalFit <= 4) {
      return "Este colaborador presenta un doble riesgo en desempeño y ajuste cultural. Se sugiere un plan de acción urgente que combine capacitación en habilidades, coaching y seguimiento intensivo para evaluar su idoneidad en el rol.";
    }
    return "Para este perfil, se recomienda una revisión detallada de sus métricas para identificar oportunidades de desarrollo personalizadas y potenciar su crecimiento dentro de la organización.";
  };

  const aiRecommendation = generateAIRecommendation(player.puntaje_desempeno, player.puntaje_fit_cultural);

  const evolutionData = {
    labels: player.evolucion_historica.map(e => e.periodo),
    datasets: [
      {
        label: 'Puntaje de Desempeño',
        data: player.evolucion_historica.map(e => e.puntaje_desempeno),
        borderColor: 'rgb(255, 99, 132)',
        backgroundColor: 'rgba(255, 99, 132, 0.5)',
        yAxisID: 'y',
      },
      {
        label: 'Puntaje de Fit Cultural',
        data: player.evolucion_historica.map(e => e.puntaje_fit_cultural),
        borderColor: 'rgb(53, 162, 235)',
        backgroundColor: 'rgba(53, 162, 235, 0.5)',
        yAxisID: 'y1',
      },
    ],
  };

  const historicalPlayerTypes = player.evolucion_historica.map(e => ({
    periodo: e.periodo,
    type: classifyPlayer(e.puntaje_desempeno, e.puntaje_fit_cultural),
  }));

  const evolutionOptions = {
    responsive: true,
    interaction: {
      mode: 'index',
      intersect: false,
    },
    stacked: false,
    plugins: {
      title: {
        display: true,
        text: 'Evolución Histórica de Puntajes',
      },
    },
    scales: {
      y: {
        type: 'linear',
        display: true,
        position: 'left',
        min: 1, // Assuming scores are 1-5
        max: 5,
        reverse: true, // Invert Y axis for performance (1=Top, 5=Low)
      },
      y1: {
        type: 'linear',
        display: true,
        position: 'right',
        grid: {
          drawOnChartArea: false,
        },
        min: 1, // Assuming scores are 1-5
        max: 5,
        reverse: true, // Invert Y axis for cultural fit (1=High, 5=Low)
      },
    },
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="player-detail-modal-title"
      aria-describedby="player-detail-modal-description"
    >
      <Box sx={style}>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
        <Typography id="player-detail-modal-title" variant="h6" component="h2" mb={2}>
          Detalle de {player.nombre} ({player.iniciales})
        </Typography>
        <Typography variant="subtitle1" mb={1}>Área: {player.area}</Typography>
        <Typography variant="subtitle1" mb={1}>Líder: {player.lider}</Typography>
        <Typography variant="subtitle1" mb={1}>Puntaje de Desempeño (Actual): {player.puntaje_desempeno}</Typography>
        <Typography variant="subtitle1" mb={2}>Puntaje de Fit Cultural (Actual): {player.puntaje_fit_cultural}</Typography>

        <Divider sx={{ my: 2 }} />

        <Typography variant="h6" mb={1}>Última Evaluación:</Typography>
        <Typography variant="body1" mb={1}>Período: {player.ultima_evaluacion.periodo}</Typography>
        <Typography variant="body1" mb={1}>Puntuación Cualitativa: {player.ultima_evaluacion.puntuacion_cualitativa}</Typography>
        <Typography variant="body1" mb={1}>Fortalezas: {player.ultima_evaluacion.fortalezas}</Typography>
        <Typography variant="body1" mb={2}>Áreas de Mejora: {player.ultima_evaluacion.areas_mejora}</Typography>

        <Divider sx={{ my: 2 }} />

        <Typography variant="h6" mb={1}>Evolución del Tipo de Jugador:</Typography>
        <Box mb={2}>
          {historicalPlayerTypes.length > 0 ? (
            historicalPlayerTypes.map((entry, index) => (
              <Typography key={index} variant="body1">
                {entry.periodo}: {entry.type}
              </Typography>
            ))
          ) : (
            <Typography variant="body2" color="text.secondary">No hay historial de tipo de jugador disponible.</Typography>
          )}
        </Box>

        <Divider sx={{ my: 2 }} />

        <Typography variant="h6" mb={1}>Evolución Histórica:</Typography>
        <Box sx={{ height: '250px', mb: 2 }}>
          <Line options={evolutionOptions} data={evolutionData} />
        </Box>

        <Divider sx={{ my: 2 }} />

        <Typography variant="h6" mb={1}>Historial de Comentarios del Delivery Manager:</Typography>
        {player.feedback_textual && Array.isArray(player.feedback_textual) && player.feedback_textual.length > 0 ? (
          player.feedback_textual.map((feedback, index) => (
            <Box key={index} mb={1}>
              <Typography variant="body2">Período: {feedback.periodo}</Typography>
              <Typography variant="body2">Autor: {feedback.autor}</Typography>
              <Typography variant="body2">Comentario: {feedback.comentario}</Typography>
            </Box>
          ))
        ) : (
          <Typography variant="body2" color="text.secondary">No hay comentarios del Delivery Manager disponibles.</Typography>
        )}

        <Divider sx={{ my: 2 }} />

        <Typography variant="h6" mb={1}>Recomendación de IA (Simulada):</Typography>
        <Typography variant="body1" sx={{ fontStyle: 'italic' }}>
          {aiRecommendation}
        </Typography>

        <Button
          variant="contained"
          color="primary"
          sx={{ mt: 3 }}
          onClick={() => onViewDetails(player.ultima_evaluacion)}
        >
          Ver Detalle
        </Button>

      </Box>
    </Modal>
  );
} 